<template>
  <div>
    <!-- Modalità automatica -->
    <template v-if="mode === 'auto'">
      <PFRow size="7x5" :separator="form_in_line"
             :label-class="form_in_line ? 'big':''"
             :label="$pgettext('priceList.pack.label','First photo price')"
             :help-text="$pgettext('priceList.pack.help', 'Set the selling price of the first photo')">
        <PCurrencyInput v-model="configuration.first_photo_price" :currency="currency"/>
      </PFRow>

      <PFRow size="7x5" :separator="form_in_line"
             :label-class="form_in_line ? 'big':''"
             :label="$pgettext('priceList.pack.label','Complete package')"
             :help-text="$pgettext('priceList.pack.help', 'Choose how many photos make a complete package and its price')"
      >
        <CRow>
          <CCol sm="4">
            <label class="">{{ $pgettext('priceList.pack.label', 'N° photos') }}</label>
            <CInput type="number" min="2" :max="maxPackNum" :value="configuration.complete_set_num" @input="onSelectNum"/>
          </CCol>
          <CCol sm="8">
            <label class="">{{ $pgettext('priceList.pack.label', 'Package price') }}</label>
            <PCurrencyInput v-model="configuration.complete_set_price" :currency="currency"/>
          </CCol>
        </CRow>
      </PFRow>

      <PFRow size="7x5" :separator="form_in_line"
             :label-class="form_in_line ? 'big':''"
             :label="$pgettext('priceList.pack.label','Discount from (no. of photos)')"
             :help-text="$pgettext('priceList.pack.help', 'Photos below this quantity are sold at full price; the package discount applies only from this number of photos onwards')"
      >
        <CRow>
          <CCol sm="4">
            <CInput type="number" min="2" :max="configuration.complete_set_num || maxPackNum" :value="configuration.discount_from_qty" @input="onSelectDiscountFrom"/>
          </CCol>
        </CRow>
      </PFRow>

      <PAlert v-if="isFirstPhotoPriceTooLow" color="warning">
        {{
          $pgettext('priceList.pack.validation', 'The first photo price must be higher than the average per-photo price of the complete package, otherwise the per-photo price would increase with quantity.')
        }}
      </PAlert>
      <PAlert v-else-if="isThresholdInvalid" color="warning">
        {{
          $pgettext('priceList.pack.validation', 'The discount threshold is too high for this package: reduce it or increase the package price.')
        }}
      </PAlert>

    </template>

    <!-- Modalità manuale -->
    <template v-if="mode === 'manual'">
      <CRow>
        <CCol lg="6">
          <h6 class="font-weight-semi-bold mt-3">
            {{ $pgettext('priceList.pack.manual', 'Complete price list') }}
          </h6>
          <p class="text-help-dark small">
            {{
              $pgettext('priceList.pack.manual', 'The price list has been applied based on your choices. You can modify the prices generated by our algorithm.')
            }}
          </p>
        </CCol>
        <CCol sm="12" :lg="{size: '4', offset:'2' }">
          <CDataTable
            class="table-price-list"
            :items="items"
            :fields="manualFields"
            :hover="false"
            :striped="false"
            :bordered="true"
            :fixed="false"
            add-table-classes="mb-0"
          >
            <template #qty="{ item }">
              <td>
                <CInput disabled :value="item.qty" />
              </td>
            </template>
            <template #price="{ item, index }">
              <td>
                <PCurrencyInput
                  v-if="isEditableRow(index)"
                  :value="item.price"
                  :currency="currency"
                  :is-valid="isInvalidRow(index) ? false : null"
                  @input="(val) => onManualPriceChange(val, index)"
                  @change="(val) => onManualPriceChange(val, index)"
                />
                <PCurrencyInput
                  v-else
                  :value="item.price"
                  :currency="currency"
                  readonly
                  disabled
                />
              </td>
            </template>
          </CDataTable>

          <PAlert v-if="hasEmptyPrices" color="danger" class="mt-1">
            {{
              $pgettext('priceList.pack.validation', 'Every row needs a price. If you need fewer options, press the reset button to start over with a different number of photos.')
            }}
          </PAlert>
          <PAlert v-else-if="!isPricesValid" color="danger" class="mt-1">
            {{
              $pgettext('priceList.pack.validation', 'Prices must be increasing: each row must cost more than the previous one.')
            }}
          </PAlert>
          <PAlert v-else-if="!isPerPhotoValid" color="danger" class="mt-1">
            {{
              $pgettext('priceList.pack.validation', 'The per-photo price must decrease as quantity grows: each row must have a lower per-photo cost than the previous one.')
            }}
          </PAlert>
        </CCol>
      </CRow>


    </template>
  </div>
</template>

<script>

import PFRow from "@/domain/core/components/PFRow.vue";
import PCurrencyInput from "@/domain/core/components/PCurrencyInput.vue";
import {mapGetters} from "vuex";
import PAlert from "@/components/PAlert.vue";

export default {
  name: "PackPriceList",
  components: {PAlert, PCurrencyInput, PFRow},
  emits: ['update', 'mode-change'],
  props: {
    currency: {
      type: String,
      required: true
    },
    form_in_line: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      mode: 'auto',
      maxPackNum: 100,
      items: [],
      configuration: {
        first_photo_price: null,
        complete_set_num: null,
        complete_set_price: null,
        discount_from_qty: 2,
      }
    }
  },
  computed: {
    ...mapGetters('event', ['eventCurrency']),
    canApply() {
      const num = this.configuration.complete_set_num
      const from = parseInt(this.configuration.discount_from_qty) || 2
      const max = parseFloat(this.configuration.complete_set_price)
      const min = parseFloat(this.configuration.first_photo_price)
      return num !== null && num >= 2 && from >= 2 && from <= num
        && max > 0 && min > 0 && min < max && min > max / num && max > min * (from - 1)
    },
    isFirstPhotoPriceTooLow() {
      const num = this.configuration.complete_set_num
      const max = parseFloat(this.configuration.complete_set_price)
      const min = parseFloat(this.configuration.first_photo_price)
      return num !== null && num >= 2 && max > 0 && min > 0 && min < max && min <= max / num
    },
    isThresholdInvalid() {
      const num = this.configuration.complete_set_num
      const from = parseInt(this.configuration.discount_from_qty) || 2
      const max = parseFloat(this.configuration.complete_set_price)
      const min = parseFloat(this.configuration.first_photo_price)
      const baseOk = num !== null && num >= 2 && max > 0 && min > 0 && min < max && min > max / num
      if (!baseOk) return false
      return from > num || max <= min * (from - 1)
    },
    isPricesValid() {
      if (this.items.length < 2) return true
      for (let i = 1; i < this.items.length; i++) {
        if (parseFloat(this.items[i].price) <= parseFloat(this.items[i - 1].price))
          return false
      }
      return true
    },
    isPerPhotoValid() {
      if (this.items.length < 2) return true
      for (let i = 1; i < this.items.length; i++) {
        const prev = parseFloat(this.items[i - 1].price) / this.items[i - 1].qty
        const curr = parseFloat(this.items[i].price) / this.items[i].qty
        // Non-increasing: flat below the discount threshold, then strictly decreasing.
        if (curr > prev + 1e-6) return false
      }
      return true
    },
    hasEmptyPrices() {
      return this.items.some(item => !item.price || parseFloat(item.price) <= 0)
    },
    manualFields() {
      return [
        {key: 'qty', label: this.$pgettext('priceList.pack.table', 'N° photos'), _style: 'width:90px;' },
        {key: 'price', label: this.$pgettext('priceList.pack.table', 'Price')},
      ]
    }
  },
  mounted() {
    this.emitModeChange()
  },
  watch: {
    canApply() {
      this.emitModeChange()
    }
  },
  methods: {
    emitModeChange() {
      this.$emit('mode-change', { mode: this.mode, canApply: this.canApply })
    },
    onSelectNum(value) {
      const n = parseInt(value, 10)
      this.configuration.complete_set_num = isNaN(n) ? null : Math.min(n, this.maxPackNum)
    },
    onSelectDiscountFrom(value) {
      const n = parseInt(value, 10)
      if (isNaN(n)) {
        this.configuration.discount_from_qty = null
        return
      }
      const upper = this.configuration.complete_set_num || this.maxPackNum
      this.configuration.discount_from_qty = Math.max(2, Math.min(n, upper))
    },
    init({items, configuration}) {
      this.configuration = {
        first_photo_price: configuration.first_photo_price ?? null,
        complete_set_price: configuration.complete_set_price ?? null,
        complete_set_num: configuration.complete_set_num ?? null,
        discount_from_qty: configuration.discount_from_qty ?? 2
      }
      this.items = items

      if (items.length > 0 && this.configuration.first_photo_price !== null) {
        this.mode = 'manual'
      } else if (items.length > 0 && this.configuration.complete_set_price !== null) {
        // Backward compatibility: old format without first_photo_price
        this.configuration.first_photo_price = items[0].price
        this.mode = 'manual'
      } else {
        this.mode = 'auto'
      }
      this.emitModeChange()
    },
    isEditableRow() {
      return true
    },
    isInvalidRow(index) {
      if (this.items.length < 2) return false
      const item = this.items[index]
      const price = parseFloat(item.price)
      if (!item.price || price <= 0) return true
      const perPhoto = price / item.qty
      if (index > 0) {
        const prev = this.items[index - 1]
        const prevPrice = parseFloat(prev.price)
        const prevPerPhoto = prevPrice / prev.qty
        if (price <= prevPrice) return true
        if (perPhoto > prevPerPhoto + 1e-6) return true
      }
      if (index < this.items.length - 1) {
        const next = this.items[index + 1]
        const nextPrice = parseFloat(next.price)
        const nextPerPhoto = nextPrice / next.qty
        if (nextPrice <= price) return true
        if (nextPerPhoto > perPhoto + 1e-6) return true
      }
      return false
    },
    isValid() {
      if (this.mode === 'auto') return this.canApply
      return this.items.length > 0 && !this.hasEmptyPrices && this.isPricesValid && this.isPerPhotoValid
    },
    getPriceList() {
      return {
        items: this.items,
        configuration: this.configuration,
        settings: {
          allow_upsell: true,
          allow_preorder: false,
          cta_enabled: true,
          cta_min_qty: 0,
          cta_position: 'b'
        }
      }
    },
    generateRows() {
      const num = parseInt(this.configuration.complete_set_num)
      const min = parseFloat(this.configuration.first_photo_price)
      const max = parseFloat(this.configuration.complete_set_price)

      if (!num || !min || !max || num < 1) return false

      if (num === 1) {
        this.items = [{qty: 1, price: max}]
        return true
      }

      // Foto sotto la soglia `from`: prezzo pieno (min per foto, nessuno sconto).
      // Dalla soglia in poi i costi marginali decrescono geometricamente (m_j = min * r^j)
      // finché il totale raggiunge `max` a quota `num`. r è calcolato affinché la somma
      // dei marginali scontati = max - min*(from-1). Con from = 2 coincide col modello precedente.
      const from = parseInt(this.configuration.discount_from_qty) || 2
      const fullCount = Math.min(from - 1, num)
      const decayCount = num - fullCount
      const remaining = max - min * fullCount

      this.items = []
      let cumulative = 0

      for (let qty = 1; qty <= fullCount; qty++) {
        cumulative = min * qty
        this.items.push({qty, price: parseFloat(cumulative.toFixed(2))})
      }

      if (decayCount > 0) {
        const r = this.findDecayRate(remaining / min, decayCount)
        for (let j = 1; j <= decayCount; j++) {
          cumulative += min * Math.pow(r, j)
          const qty = fullCount + j
          const price = (qty === num) ? max : this.roundPrice(cumulative, max)
          this.items.push({qty, price})
        }
      }

      return true
    },
    findDecayRate(target, count) {
      // Trova r in (0,1) tale che r + r^2 + ... + r^count = target (somma crescente in r).
      let lo = 0, hi = 1
      for (let i = 0; i < 50; i++) {
        const mid = (lo + hi) / 2
        let sum = 0
        for (let j = 1; j <= count; j++) sum += Math.pow(mid, j)
        if (sum < target) lo = mid
        else hi = mid
      }
      return (lo + hi) / 2
    },
    roundPrice(value, maxAmount) {
      const rounding = Math.pow(10, Math.floor(Math.log10(maxAmount / 100)))
      let roundedValue = Math.round(value / rounding)
      return parseFloat((roundedValue * rounding).toFixed(2))
    },
    onApply() {
      if (!this.canApply) return
      if (!this.generateRows()) return

      this.mode = 'manual'
      this.emitModeChange()
      this.emitUpdate()
    },
    onManualPriceChange(val, index) {
      this.$set(this.items[index], 'price', val)
      this.emitUpdate()
    },
    onResetToAuto() {
      this.items = []
      this.configuration = {
        first_photo_price: null,
        complete_set_num: null,
        complete_set_price: null,
        discount_from_qty: 2,
      }
      this.mode = 'auto'
      this.emitModeChange()
      this.emitUpdate()
    },
    emitUpdate() {
      this.$emit('update', {
        items: this.items,
        configuration: this.configuration,
        settings: {
          allow_upsell: true,
          allow_preorder: false,
          cta_enabled: true,
          cta_min_qty: 0,
          cta_position: 'b'
        }
      })
    }
  }
}
</script>
